import React, { useEffect, useMemo, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import _ from "lodash";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Box, Grid } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import {
  Button,
  SIZE,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import {
  selectEcosystemList,
  selectEnabledEcosystems,
  selectEntityChainList,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import {
  alertServerError,
  getChainName,
  getEcosystemName,
} from "features/helpers/utils";
import WalletCard from "./WalletCard";
import TokenHoldingTable from "./TokenHoldingTable";
import NFTHoldingTable from "./NFTHoldingTable";
import SelectedWalletsInfo from "./SelectedWalletsInfo";
import { StyledTitle, StyledSliderButton } from "./UserInfo.style";
import axios from "axios";
import { apiUrl } from "features/configure";
import { StyledSkeleton } from "components/LoadingComponent/LoadingComponent.styles";

const MAX_RETRY = 10;

const UserOnchainData = ({
  selectedUserIds,
  isTicketUserInfo,
  noOnchainData,
}) => {
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const enabledEcosystems = useSelector(selectEnabledEcosystems);
  const ecosystemList = useSelector(selectEcosystemList);
  const chainList = useSelector(selectEntityChainList);

  const [userOnchainWallets, setUserOnchainWallets] = useState([]);
  const [ecosystemFilter, setEcosystemFilter] = useState({
    name: formatMessage({ id: "global.filter.allEcosystem" }),
    value: "",
  });
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [selectedChainIds, setSelectedChainIds] = useState([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const dispatch = useDispatch();

  const resetState = () => {
    setSelectedWallets([]);
    setSelectedChainIds([]);
    setEcosystemFilter({
      name: formatMessage({ id: "global.filter.allEcosystem" }),
      value: "",
    });
  };

  const ecosystemOptions = [
    {
      name: formatMessage({ id: "global.filter.allEcosystem" }),
      label: formatMessage({ id: "global.filter.allEcosystem" }),
      value: "",
    },
    ...enabledEcosystems
      ?.map((item) => ecosystemList.find((eco) => eco.value === item))
      .filter((item) => item),
  ];

  const fetchUserOnchainData = async () => {
    if (!selectedUserIds?.[0]) return;
    try {
      setIsLoading(true);
      const result = await axios.get(
        `${apiUrl}/api/user/${entityName}/onchainData`,
        {
          params: {
            userIds: selectedUserIds,
          },
        }
      );
      setUserOnchainWallets(result.data);
    } catch (err) {
      dispatch(alertServerError(err));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchUserOnchainData();
    return () => {
      setUserOnchainWallets([]);
      resetState();
    };
  }, [selectedUserIds?.[0]]);

  const refreshOnchainData = async () => {
    const walletAddresses = _.map(userOnchainWallets, "address");
    if (walletAddresses.length === 0) return;

    try {
      setIsLoading(true);
      const result = await axios.post(`${apiUrl}/api/user/refresh`, {
        entityName,
        addresses: walletAddresses,
      });

      const allFetched = result?.data?.every((wallet) => wallet.dataFetched);

      if (allFetched) {
        setRetryCount(0);
        fetchUserOnchainData();
      } else if (retryCount < MAX_RETRY) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => {
          refreshOnchainData();
        }, 10000);
      } else {
        setRetryCount(0);
        dispatch(
          alertServerError({
            message: `Failed to fetch complete data after ${MAX_RETRY} attempts.`,
          })
        );
        setIsLoading(false);
      }
    } catch (err) {
      dispatch(alertServerError(err));
      setIsLoading(false);
    }
  };

  const swiperRef = useRef(null);
  // useEffect(() => {
  //   if (swiperRef.current && matchedWalletIndex !== null) {
  //     swiperRef.current.slideTo(matchedWalletIndex, 0); // 立刻滑過去
  //   }
  // }, [matchedWalletIndex]);

  const allChains = useMemo(() => {
    return _.uniq(_.flatten(_.map(userOnchainWallets, (w) => w.chains)));
  }, [userOnchainWallets]);

  const filteredWallets = useMemo(() => {
    return userOnchainWallets
      ?.filter((wallet) => {
        const matchEco =
          !ecosystemFilter ||
          wallet.ecosystem === ecosystemFilter.value ||
          ecosystemFilter.value === ""; // All Ecosystems
        return matchEco;
      })
      ?.map((wallet) => {
        const ecosystemName = getEcosystemName(wallet.ecosystem);

        const chainNames = wallet.chains
          ?.map((chainId) => getChainName(chainId))
          ?.filter(Boolean);

        return {
          ...wallet,
          ecosystem: ecosystemName,
          chains: chainNames,
        };
      });
  }, [userOnchainWallets, ecosystemFilter, ecosystemList, chainList]);

  // isTicketUserInfo: make first wallet selected by default
  // not isTicketUserInfo: show all wallets by default
  const wallets = useMemo(() => {
    if (isTicketUserInfo) {
      const wallet = filteredWallets?.[activeSlideIndex];
      return wallet ? [wallet] : [];
    }

    return selectedWallets?.length > 0 ? selectedWallets : filteredWallets;
  }, [isTicketUserInfo, filteredWallets, activeSlideIndex, selectedWallets]);
  const walletsIdArray = useMemo(() => {
    return JSON.stringify(wallets?.map((wallet) => wallet.id));
  }, [wallets]);

  const handleWalletClick = (wallet) => {
    setSelectedChainIds([]); // clear selected chain ids when wallet is clicked
    setSelectedWallets((prevSelected) => {
      const isAlreadySelected = prevSelected.some((w) => w.id === wallet.id);

      if (isAlreadySelected) {
        // unselect
        return prevSelected?.filter((w) => w.id !== wallet.id);
      } else {
        // select
        return [...prevSelected, wallet];
      }
    });
  };

  const renderRefreshButton = () => (
    <Button
      size={SIZE.XSS}
      variant={VARIANT.TEXT}
      startIcon={
        <i
          className="meta-crm-icon-ic_refresh font-size-24"
          style={{ marginRight: "-4px" }}
        />
      }
      onClick={refreshOnchainData}
    >
      {_.startCase(formatMessage({ id: "global.btn.refresh" }))}
    </Button>
  );

  const renderLoadingCard = (sx = {}) => (
    <StyledSkeleton animation="wave" sx={{ borderRadius: "8px", ...sx }} />
  );

  const RenderUserWalletContents = () => (
    <>
      {/* Filter Section */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={4}
        mb={1}
      >
        <StyledTitle
          sx={{ display: "flex", alignItems: "center", marginBottom: "0" }}
        >
          {formatMessage({ id: "onchainData.userWallet.title" })}
          {renderRefreshButton()}
        </StyledTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            sx={{
              color: "#7B61FF",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "12px",
            }}
            onClick={() => {
              resetState();
            }}
          >
            {formatMessage({ id: "global.btn.clearSelection" })}
          </Box>
          <Box sx={{ width: "180px" }} className="general-shadow">
            <AutoCompleteDropdown
              value={ecosystemFilter.name}
              size="medium"
              options={ecosystemOptions}
              onChange={(e, v) => {
                setEcosystemFilter(v);
                setSelectedWallets([]);
              }}
              placeholder={formatMessage({ id: "global.filter.select" })}
              noBorder
            />
          </Box>
        </Box>
      </Box>

      {/* Selected Wallets Info Section */}
      {isLoading ? (
        renderLoadingCard({ height: "49px" })
      ) : (
        <SelectedWalletsInfo
          wallets={wallets}
          isAnyWalletSelected={selectedWallets?.length > 0}
          ecosystemFilter={ecosystemFilter}
          selectedChainIds={selectedChainIds}
          setSelectedChainIds={setSelectedChainIds}
          isLoading={isLoading}
        />
      )}

      {/* Wallet Cards */}
      <Grid
        container
        rowSpacing={"8px"}
        columnSpacing={"12px"}
        mt={"0px"}
        sx={{ alignItems: "stretch" }}
      >
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={4}
                key={`skeleton-${index}`}
                sx={{ display: "flex" }}
              >
                {renderLoadingCard({ height: "230px" })}
              </Grid>
            ))
          : filteredWallets?.map((wallet, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={4}
                key={wallet.id}
                sx={{ display: "flex" }}
              >
                <WalletCard
                  key={index}
                  wallet={wallet}
                  isSelected={selectedWallets?.some((w) => w.id === wallet.id)}
                  onClick={() => handleWalletClick(wallet)}
                  index={index}
                />
              </Grid>
            ))}
      </Grid>
    </>
  );

  const RenderTicketWalletContents = () => {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const selectedWalletInfoRef = useRef(null);

    return (
      <Box position="relative">
        {/* Navigation Buttons */}
        <StyledSliderButton
          ref={prevRef}
          side="left"
          show={activeSlideIndex !== 0}
        >
          <ChevronLeft sx={{ color: "#7B61FF" }} />
        </StyledSliderButton>

        <StyledSliderButton
          ref={nextRef}
          side="right"
          show={
            !(
              isLoading ||
              activeSlideIndex === filteredWallets.length - 1 ||
              filteredWallets.length === 0
            )
          }
        >
          <ChevronRight sx={{ color: "#7B61FF" }} />
        </StyledSliderButton>

        {/* Wallet Cards Swiper */}
        <Swiper
          modules={[Navigation]}
          slidesPerView={1}
          centeredSlides
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          onSlideChange={(swiper) => {
            const newWallet = filteredWallets[swiper.activeIndex];
            setSelectedWallets([newWallet]);
            setActiveSlideIndex(swiper.activeIndex);
          }}
          style={{
            padding: "10px",
            paddingBottom: "18px",
            margin: "-10px",
            marginBottom: "-2px",
            borderRadius: "8px",
          }}
        >
          {filteredWallets?.map((wallet, index) => (
            <SwiperSlide key={wallet.id}>
              {isLoading ? (
                renderLoadingCard({
                  height: "218.75px",
                  width:
                    selectedWalletInfoRef.current &&
                    selectedWalletInfoRef.current.offsetWidth,
                })
              ) : (
                <WalletCard
                  wallet={wallet}
                  isSelected={false} // dont show border wether it is selected or not
                  onClick={() => handleWalletClick(wallet)}
                  index={index}
                  renderRefreshButton={renderRefreshButton}
                  sx={{
                    width:
                      selectedWalletInfoRef.current &&
                      selectedWalletInfoRef.current.offsetWidth,
                  }}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        {/* Selected Wallets Info Section */}
        {isLoading ? (
          renderLoadingCard({ height: "49px" })
        ) : (
          <SelectedWalletsInfo
            wallets={wallets}
            isAnyWalletSelected={selectedWallets?.length > 0}
            ecosystemFilter={ecosystemFilter}
            selectedChainIds={selectedChainIds}
            setSelectedChainIds={setSelectedChainIds}
            isTicketUserInfo={isTicketUserInfo}
            selectedWalletInfoRef={selectedWalletInfoRef}
            isLoading={isLoading}
          />
        )}
      </Box>
    );
  };

  const renderOnchainDetail = () => {
    if (noOnchainData || isLoading) return null;

    return (
      <Box>
        {" "}
        {/* Token Holdings */}
        <Box mt={4} mb={4}>
          <StyledTitle>
            {formatMessage({ id: "onchainData.tokenHoldings.title" })}
          </StyledTitle>
          <TokenHoldingTable
            entityName={entityName}
            walletsIdArray={walletsIdArray}
            selectedChainIds={
              selectedChainIds?.length > 0 ? selectedChainIds : allChains
            }
            isUserOnchainDataLoading={isLoading}
          />
        </Box>
        {/* NFT Holdings */}
        <Box>
          <StyledTitle>
            {formatMessage({ id: "onchainData.nftHoldings.title" })}
          </StyledTitle>
          <NFTHoldingTable
            entityName={entityName}
            walletsIdArray={walletsIdArray}
            selectedChainIds={
              selectedChainIds?.length > 0 ? selectedChainIds : allChains
            }
            isUserOnchainDataLoading={isLoading}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={isTicketUserInfo && { padding: "16px", backgroundColor: "#FAFAFD" }}
    >
      {noOnchainData ? (
        <Box
          width="100%"
          className="item-flex"
          justifyContent="center"
          height={`calc(100vh - ${isTicketUserInfo ? "163px" : "80px"})`}
          flexDirection="column"
        >
          <img
            src={require("assets/img/barChartAlt.png")}
            style={{ filter: "grayscale(1)" }}
          />
          <Box color="#555" fontWeight={700} m="12px 0 8px">
            {formatMessage({
              id: "emptyState.onchainData.title",
            })}
          </Box>
          <Box textAlign="center" color="#777" width="188px">
            {formatMessage({
              id: "emptyState.onchainData.description",
            })}
          </Box>
        </Box>
      ) : (
        <>
          {/* Wallet Contents */}
          {isTicketUserInfo
            ? RenderTicketWalletContents()
            : RenderUserWalletContents()}
          {renderOnchainDetail()}
        </>
      )}
    </Box>
  );
};

export default UserOnchainData;
